import { AssetsProvider, Image, styled, Text, useQuery } from '@obvio/app'
import { Loading, Stack } from '@obvio/ui'
import { ascSort } from '@obvio/utils'
import { useCallback, useMemo } from 'react'

import {
  CarouselBase,
  CarouselDivider,
  CarouselImage,
  CarouselSection,
} from './Carousels/CarouselBase'
import { useProfitRoom } from '@/components/useProfitRoom'
import { QUERIES } from '@/constants/queries'

import type { ImageAsset } from '@obvio/app'
import type { AllowNullishForOptional } from '@obvio/utils'
import type { ReactElement } from 'react'

const EventWrap = styled(Stack)`
  width: 25.875rem;
  height: 100%;
  color: ${(theme) => theme.colors.secondary};
  text-align: left;
`

type Room = AllowNullishForOptional<{
  id: string
  title: string
  image: ImageAsset
  description: string
  profitroomId?: string
}>

const EventTitle = styled(Text)`
  font-size: 1.25rem;
  line-height: 100%;
  font-family: ${(theme) => theme.font};
`

const Description = styled(Text)`
  font-size: 1rem;
`

const ButtonWrapper = styled.button`
  all: unset;
  cursor: pointer;
  transition: all 0.2s;
  :hover {
    opacity: 0.5;
  }
`

type RoomProps = {
  event: Room
}

function RoomComp({ event }: RoomProps): ReactElement {
  const { bookingOpen } = useProfitRoom()

  return (
    <EventWrap kind="vertical" spacing="medium">
      <CarouselImage>
        <Image context={event.id} img={event.image} />
      </CarouselImage>
      <CarouselDivider />
      <ButtonWrapper
        onClick={() =>
          event.profitroomId &&
          bookingOpen?.({
            RoomId: event.profitroomId,
          })
        }
      >
        <Stack kind="vertical">
          <EventTitle tag="h3">{event.title}</EventTitle>
          <Description>{event.description}</Description>
        </Stack>
      </ButtonWrapper>
    </EventWrap>
  )
}

export function Rooms(): ReactElement {
  const { data: rooms, loading } = useQuery('getProfitroomRooms', {
    select: QUERIES.rooms.getProfitroomRooms,
  })

  const comp = useCallback(
    (room: Room): ReactElement => <RoomComp event={room} />,
    [],
  )

  const dataWithSingleImage = useMemo(
    () =>
      (rooms ?? [])
        .map(({ images, ...room }) => ({
          ...room,
          image: images[0],
        }))
        .sort((a, b) =>
          ascSort(
            (a.category.order ?? 0) * 100 + (a.order ?? 0),
            (b.category.order ?? 0) * 100 + (b.order ?? 0),
          ),
        ),
    [rooms],
  )

  if (loading) {
    return <Loading />
  }

  return (
    <AssetsProvider path="/room">
      <CarouselSection>
        <CarouselBase data={dataWithSingleImage} render={comp} />
      </CarouselSection>
    </AssetsProvider>
  )
}
